import { Form } from "antd";
import { type FormItemProps, type FormProps } from "antd/lib/form";
import { ErrorMessage, FormikConsumer, type FormikContext } from "formik";
import { get } from "lodash-es";
import { Component, memo, type ReactNode, useMemo } from "react";

import { BTFormItem as BTFormItemCore } from "@btnet/core-components/BTFormItem";
import { cn } from "@btnet/utilities-core";

import { getValidateStatusTouched } from "utilities/form/form";
import yup, { getFieldSchema, isFieldRequired } from "utilities/form/yup";
import { isNullOrUndefined } from "utilities/object/object";
import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { RequiredFieldIndicator } from "commonComponents/utilities/RequiredFieldIndicator/RequiredFieldIndicator";

import "./BTForm.less";

export interface IBTFormProps extends FormProps {
    onSubmit?: () => void;
    children?: ReactNode;
}

export class BTForm extends Component<IBTFormProps> {
    render() {
        const { onSubmit, ...otherProps } = this.props;
        return (
            // eslint-disable-next-line react/forbid-elements
            <Form layout="vertical" onFinish={onSubmit} {...otherProps} />
        );
    }
}

export interface IBTFormItemProps extends FormItemProps {
    children?: ReactNode;
    ariaLabel?: string;
}

export class BTFormItem extends Component<IBTFormItemProps> {
    render() {
        const { className, ariaLabel, ...otherProps } = this.props;
        return (
            // eslint-disable-next-line react/forbid-elements
            <Form.Item
                {...otherProps}
                className={cn(className, "BTFormItem")}
                aria-label={ariaLabel}
            />
        );
    }
}

export interface ILabelSchema {
    label: string;
    isRequiredFieldIndicatorVisible: boolean;
}

interface IBTFormItemAutomaticProps<FormValues> {
    /**
     * key name of the field, lodash deep names supported (example: "group.field", or "list[3].field") note: generic cannot be used when using a sub-key
     * id of the field
     */
    id: KeyOfOrString<FormValues> & string;

    /**
     * Set to true if the input being wrapped readOnly. If true, the required field indicator will not be displayed
     * @default false
     */
    readOnly?: boolean;

    /**
     * @default
     * yup validation schema's label, conditionally adds
     * when null no label is used
     */
    label?: null | string | JSX.Element | ((schema: ILabelSchema) => React.ReactNode);

    /**
     * Override for the validator-based required detection.
     * Can use to manually set if the asterisk should be displayed or not.
     */
    required?: boolean;

    className?: string;

    extra?: string;

    /**
     * Set the label size
     * medium: 12px
     * large: 16px
     * @default "medium"
     */
    labelSize?: "medium" | "large";

    children: React.ReactNode;
    help?: React.ReactNode;
    ariaLabel?: string;
    useBds?: boolean;
    tooltip?: string; // To be used with usBds=true only
    /**
     * Only used if useBds is true otherwise the prop is ignored.
     * Sets the label alignment for the form item.
     * @default "top"
     */
    labelAlign?: "left" | "top";
}

function isYupSchema<T extends object | undefined>(
    schema: any,
): schema is yup.ObjectSchema<NonNullable<T>> {
    return schema !== undefined && yup.isSchema(schema);
}

/**
 * @example <BTFormItemAutomatic<FormValues> id="field" />
 * @example <BTFormItemAutomatic id="object.field" />
 * @example <BTFormItemAutomatic id="array[0].field" />
 */
export const BTFormItemAutomatic = memo(
    BTFormItemAutomaticInternal,
) as typeof BTFormItemAutomaticInternal;

function BTFormItemAutomaticInternal<FormValues extends object | undefined = undefined>(
    props: IBTFormItemAutomaticProps<FormValues>,
) {
    return (
        <FormikConsumer>
            {function FormikConsumerChildren(formikProps) {
                return <FormItemFormikConsumer<FormValues> {...formikProps} {...props} />;
            }}
        </FormikConsumer>
    );
}

type IFormItemFormikConsumerProps<FormValues> = FormikContext<FormValues> &
    IBTFormItemAutomaticProps<FormValues>;
function FormItemFormikConsumer<FormValues extends object | undefined = undefined>({
    touched,
    errors,
    validationSchema,
    setFieldValue,
    readOnly,
    values,
    id,
    label,
    required,
    className,
    extra,
    labelSize,
    children,
    help,
    ariaLabel,
    useBds = false,
    labelAlign,
    tooltip,
}: IFormItemFormikConsumerProps<FormValues>) {
    const schema = useMemo(
        () => (typeof validationSchema === "function" && validationSchema()) || validationSchema,
        [validationSchema],
    );

    const isRequired = required ?? isFieldRequired(schema, id, values);
    const fieldSchemaMetaData = getFieldSchema(schema, id, values);
    const labelToUse = useMemo(() => {
        let labelToUse;
        if (label === undefined) {
            if (fieldSchemaMetaData.label === undefined && (!readOnly || !isRequired)) {
                return "";
            }
            labelToUse = (
                <>
                    {fieldSchemaMetaData.label}
                    {!readOnly && isRequired && <RequiredFieldIndicator />}
                </>
            );
        } else if (typeof label === "function") {
            labelToUse = label({
                label: fieldSchemaMetaData.label,
                isRequiredFieldIndicatorVisible: !readOnly && isRequired,
            });
        } else if (label !== null) {
            labelToUse = label;
        }
        return labelToUse;
    }, [fieldSchemaMetaData.label, isRequired, label, readOnly]);

    // Arbitrarily picked `setFieldValue` to determine if we are in a formik form.  Picked this since it's a common prop provided by formik.
    if (!setFieldValue) {
        throw new Error("Must use FormItemAutomatic in a Formik Form");
    }

    if (!isYupSchema<FormValues>(schema)) {
        throw new Error("Must provide a yup schema!");
    }

    if (useBds) {
        return (
            <BTFormItemCore
                label={labelToUse}
                htmlFor={id}
                validateStatus={
                    touched !== undefined && get(touched, id) !== undefined
                        ? getValidateStatusTouched(errors, touched, id)
                        : undefined
                }
                help={help ?? <ErrorMessage name={id} />}
                className={cn(className, {
                    largeLabel: labelSize === "large",
                })}
                extra={extra}
                ariaLabel={ariaLabel}
                labelAlign={labelAlign}
                tooltip={tooltip}
            >
                {children}
            </BTFormItemCore>
        );
    }

    return (
        <BTFormItem
            label={labelToUse}
            htmlFor={id}
            validateStatus={
                touched !== undefined && get(touched, id) !== undefined
                    ? getValidateStatusTouched(errors, touched, id)
                    : undefined
            }
            help={help ?? <ErrorMessage name={id} />}
            className={cn(className, {
                largeLabel: labelSize === "large",
            })}
            extra={extra}
            ariaLabel={ariaLabel}
        >
            {children}
        </BTFormItem>
    );
}

export function btValidateSync<T>(
    schema: yup.Schema<T>,
    value: T,
): yup.ValidationError | undefined {
    let errors: yup.ValidationError | undefined = undefined;

    try {
        schema.validateSync(value);
        errors = undefined;
    } catch (e) {
        errors = e as yup.ValidationError;
    }

    return errors;
}

function btValidateSyncAt<T>(
    schema: yup.Schema<T>,
    field: string,
    value: T,
): yup.ValidationError | undefined {
    let errors: yup.ValidationError | undefined = undefined;

    try {
        schema.validateSyncAt(field, { [field]: value } as T);
        errors = undefined;
    } catch (e) {
        errors = e as yup.ValidationError;
    }

    return errors;
}

const BTFormItemLabel = (props: { label: string; required: boolean; readOnly: boolean }) => {
    const { label, required, readOnly } = props;
    return (
        <>
            {label}
            {!readOnly && required && <RequiredFieldIndicator />}
        </>
    );
};

interface IBTFormItemYupProps {
    label: React.ReactNode;
    help: string | undefined;
    validateStatus: "error" | undefined;
}

// showLabel can be cleaned-up with the PC - Allowance on Worksheet flag
export function getBTFormItemYupValidationProps<T>(
    value: T,
    schema: yup.Schema<unknown>,
    field: string,
    readOnly?: boolean,
    showLabel?: boolean,
): IBTFormItemYupProps {
    const currentErrors = btValidateSyncAt(schema, field, value);

    const required = isFieldRequired(schema, field, value);
    const fieldSchemaMetaData = getFieldSchema(schema, field, value);
    showLabel = showLabel ?? true;

    return {
        label: showLabel && (
            <BTFormItemLabel
                label={fieldSchemaMetaData.label}
                required={required}
                readOnly={!!readOnly}
            />
        ),
        help: currentErrors?.message,
        validateStatus: !isNullOrUndefined(currentErrors) ? "error" : undefined,
    };
}
